import { AvField, AvForm } from "availity-reactstrap-validation";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { MutatingDots } from "react-loader-spinner";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { Bounce, ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Button, Card, CardBody, Col, Label, Row } from "reactstrap";
import api from "../../services/api";
import { setBreadcrumbItems } from "../../store/actions";

const EditCourseProgramName = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  const breadcrumbItems = [
    { title: "Technical Hub", link: "#" },
    { title: "Course Program Names", link: "#" },
    { title: "Edit Course Program Name", link: "#" },
  ];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [courses, setCourses] = useState([]); // from get-courses
  const [passoutYears, setPassoutYears] = useState([]); // from get-passout_year (expects [{value,label}...])

  const [selectedCourse, setSelectedCourse] = useState(null); // { label, value }
  const [selectedPassoutYear, setSelectedPassoutYear] = useState(null); // { label, value }

  const [programName, setProgramName] = useState(""); // controlled
  const [addedBy, setAddedBy] = useState("admin");

  const allowedRegex = /^[a-zA-Z0-9\s.\-]*$/; // same as before

  useEffect(() => {
    // breadcrumbs + current user
    if (props?.setBreadcrumbItems) props.setBreadcrumbItems("Course Program Names", breadcrumbItems);

    try {
      const auth = JSON.parse(localStorage.getItem("authUser"));
      if (auth?.first_name) setAddedBy(auth.first_name);
    } catch (e) {}

    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      // fetch courses, years, record (API returns arrays per your data)
      const [coursesRes, yearsRes, recRes] = await Promise.all([
        axios.get(`${backendUrl}get-courses`),
        axios.get(`${backendUrl}get-passout_year`),
        api.get(`${backendUrl}get-course-program-name-by-id/${id}`),
      ]);

      // courses: array of objects { _id, course_name, ... }
      const coursesData = coursesRes.status === 200 ? (coursesRes.data || []) : [];
      setCourses(coursesData);

      // passoutYears: array of { value, label }
      const yearsData = yearsRes.status === 200 ? (yearsRes.data || []) : [];
      setPassoutYears(yearsData);

      // record: API returns an array with single object (per your example)
      const recArray = recRes.status === 200 ? (recRes.data || []) : [];
      const item = Array.isArray(recArray) ? recArray[0] : recArray;

      if (!item) {
        toast.error("Record not found.");
        return;
      }

      // program name controlled state
      setProgramName(item.program_name ?? "");

      // --- Prefill course: find matching course option from coursesData ---
      // course_id in record is the _id of a course in coursesData
      const matchedCourse = coursesData.find((c) => c._id === item.course_id);
      if (matchedCourse) {
        setSelectedCourse({ label: matchedCourse.course_name, value: matchedCourse._id });
      } else {
        // fallback: use names from record (ensures Select still shows something)
        setSelectedCourse({ label: item.course_name ?? item.course_id, value: item.course_id });
      }

      // --- Prefill passout year: find matching option from yearsData ---
      const matchedYear = yearsData.find((y) => {
        // passout years in yearsData may have numeric or string values
        return String(y.value) === String(item.passout_year);
      });
      if (matchedYear) {
        setSelectedPassoutYear(matchedYear);
      } else {
        // fallback: create simple option
        setSelectedPassoutYear({ label: String(item.passout_year), value: item.passout_year });
      }
    } catch (err) {
      console.error("loadAll error:", err);
      toast.error("Failed to load data. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  // options for selects
  const courseOptions = courses.map((c) => ({ label: c.course_name, value: c._id }));
  const passoutOptions = passoutYears.map((y) => ({ label: y.label, value: y.value }));

  const handleSubmit = async (e, values) => {
    // use controlled programName (don't rely on values)
    if (!selectedCourse?.value) return toast.error("Please select a course.");
    if (!selectedPassoutYear?.value) return toast.error("Please select passout year.");
    if (!programName || programName.trim() === "") return toast.error("Please enter program name.");

    const payload = {
      course_id: selectedCourse.value,
      program_name: programName.trim(),
      passout_year: selectedPassoutYear.value,
      added_by: addedBy,
    };

    try {
      setSaving(true);
      const res = await api.put(`${backendUrl}edit-course-program-name/${id}`, payload);
      if (res.status === 200 || res.status === 201) {
        toast.success(res.data?.message || "Program name updated successfully!");
        // navigate after short delay to let toast show
        setTimeout(() => navigate("/course-program-names-list"), 800);
      } else {
        toast.error("Unexpected server response.");
      }
    } catch (err) {
      console.error("Update error:", err);
      toast.error(err?.response?.data?.message || "Failed to update. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <MutatingDots
          visible
          height="100"
          width="100"
          color="#2B3A4A"
          secondaryColor="#2B3A4A"
          radius="12.5"
          ariaLabel="mutating-dots-loading"
        />
      </div>
    );
  }

  return (
    <React.Fragment>
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} transition={Bounce} />

      <Row>
        <Col xl="12">
          <Card>
            <CardBody>
              <h4 className="card-title">Edit Course Program Name</h4>

              <AvForm onValidSubmit={handleSubmit}>
                <Row>
                  <Col md="4">
                    <div className="mb-3">
                      <Label>Select Course *</Label>
                      <Select
                        value={selectedCourse}
                        onChange={(val) => setSelectedCourse(val)}
                        options={courseOptions}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        placeholder="Select course"
                      />
                    </div>
                  </Col>

                  <Col md="4">
                    <div className="mb-3">
                      <Label>Select Passout Year *</Label>
                      <Select
                        value={selectedPassoutYear}
                        onChange={(val) => setSelectedPassoutYear(val)}
                        options={passoutOptions}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        placeholder="Select passout year"
                      />
                    </div>
                  </Col>

                  <Col md="4">
                    <div className="mb-3">
                      <Label>Program Name *</Label>
                      <AvField
                        name="programName"
                        type="text"
                        value={programName}
                        onChange={(e) => setProgramName(e.target.value)}
                        validate={{
                          required: { value: true, errorMessage: "Enter program name" },
                        }}
                        maxLength="50"
                        onKeyDown={(e) => {
                          const key = e.key;
                          if (!allowedRegex.test(key) && key.length === 1) e.preventDefault();
                        }}
                        onPaste={(e) => {
                          const paste = e.clipboardData.getData("text");
                          if (!allowedRegex.test(paste)) e.preventDefault();
                        }}
                      />
                    </div>
                  </Col>
                </Row>

                <Button color="primary" type="submit" className="mt-3" disabled={saving}>
                  {saving ? "Saving..." : "Update"}
                </Button>

                <Button color="secondary" className="mt-3 ms-2" onClick={() => navigate("/course-program-names-list")}>
                  Cancel
                </Button>
              </AvForm>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default connect(null, { setBreadcrumbItems })(EditCourseProgramName);
